import {
  Globe,
  ShieldAlert,
  TrendingUp,
  Scale,
  BadgeCheck,
  Gavel,
  Building2,
  Landmark,
} from "lucide-react";

const BENEFITS = [
  {
    icon: Globe,
    title: "Exclusive Nationwide Rights",
    description:
      "Federal trademark registration grants you exclusive, nationwide rights to use your mark in connection with the goods and services listed in your registration. Unlike common law rights, which are limited to the geographic area where you actually conduct business, a registered trademark provides coast-to-coast protection across all 50 states and US territories from the date of filing. This means no other business can legally use a confusingly similar mark anywhere in the country.",
  },
  {
    icon: ShieldAlert,
    title: "Prevent Copycats & Infringers",
    description:
      "A registered trademark serves as a powerful legal deterrent against would-be infringers and copycats. The registration symbol puts competitors on constructive notice that your mark is federally protected, significantly reducing the likelihood of unauthorized use. If infringement does occur, your registration provides the legal standing to take swift enforcement action, including filing lawsuits in federal court and seeking injunctions, damages, and attorney's fees.",
  },
  {
    icon: TrendingUp,
    title: "Increase Brand & Asset Value",
    description:
      "A federally registered trademark is a valuable intangible asset that can significantly increase the overall value of your business. Trademarks can be licensed, franchised, or sold, generating additional revenue streams for your company. Investors, venture capitalists, and potential acquirers view a strong trademark portfolio as a sign of business maturity and reduced legal risk, making your company more attractive for funding, partnerships, and exit opportunities.",
  },
  {
    icon: Scale,
    title: "Legal Presumption of Ownership",
    description:
      "Federal registration creates a legal presumption of your ownership of the mark and your exclusive right to use it nationwide. This presumption significantly strengthens your position in any trademark dispute, shifting the burden of proof to the opposing party. In court, your registration certificate serves as prima facie evidence of the validity of your mark, your ownership, and your exclusive right to use it in commerce.",
  },
  {
    icon: BadgeCheck,
    title: "Amazon Brand Registry Access",
    description:
      "A federally registered trademark is required for enrollment in Amazon Brand Registry, which provides powerful tools to protect your brand on the world's largest e-commerce platform. Brand Registry gives you access to enhanced brand content, sponsored brand advertising, and proactive brand protection tools that help you identify and report counterfeit listings. Without trademark registration, you cannot access these essential tools for protecting your online brand presence.",
  },
  {
    icon: Gavel,
    title: "Federal Court Jurisdiction",
    description:
      "Trademark registration gives you the right to bring infringement actions in federal court, which offers several advantages over state courts including broader geographic jurisdiction, specialized intellectual property expertise, and the potential for enhanced damages and attorneys' fees in cases of willful infringement. Federal court jurisdiction provides a more powerful and efficient forum for protecting your trademark rights against infringers operating across state lines.",
  },
  {
    icon: Building2,
    title: "US Customs Protection",
    description:
      "You can record your registered trademark with US Customs and Border Protection (CBP), which enables customs officers to seize imported goods that bear infringing marks at the border. This powerful enforcement tool helps prevent counterfeit products from entering the US market and reaching consumers, protecting both your brand reputation and your customers from inferior or potentially dangerous counterfeit goods.",
  },
  {
    icon: Landmark,
    title: "International Filing Priority",
    description:
      "A US trademark registration provides a foundation for seeking trademark protection in foreign countries through international treaties such as the Madrid Protocol. Your US filing date can serve as a priority date for applications filed in member countries within six months, giving you a strategic advantage when expanding your brand internationally. This international filing priority is essential for businesses planning global expansion.",
  },
];

export default function BenefitsSection() {
  return (
    <section
      id="benefits"
      data-testid="benefits-section"
      className="py-20 md:py-32 bg-[#F8F9FA]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#1E3A8A] mb-4">
            Benefits of Registration
          </span>
          <h2
            data-testid="benefits-heading"
            className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0F172A] tracking-tight mb-4"
          >
            Why Trademark Registration Is Essential for Your Business
          </h2>
          <p className="text-[#475569] text-base sm:text-lg max-w-3xl mx-auto leading-relaxed">
            Federal trademark registration offers a comprehensive suite of legal protections and business advantages that go far beyond simply putting a symbol next to your brand name. Here are the key benefits that make trademark registration one of the most important investments you can make in your business.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200 rounded-lg overflow-hidden">
          {BENEFITS.map((b, i) => (
            <div
              key={b.title}
              data-testid={`benefit-card-${i}`}
              className="bg-white p-7 hover:bg-[#F8F9FA] transition-colors duration-300"
            >
              <div className="w-10 h-10 rounded-lg bg-[#1E3A8A]/10 flex items-center justify-center mb-4">
                <b.icon className="w-5 h-5 text-[#1E3A8A]" />
              </div>
              <h3 className="font-heading text-base font-semibold text-[#0F172A] mb-3">
                {b.title}
              </h3>
              <p className="text-[#475569] text-sm leading-relaxed">{b.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
